import { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { DashboardLayout, PageHeader } from '../common/page_component/index.js';
import DateRangeFilter from './components/DateRangeFilter.js';
import { BranchPerformanceTable, TargetProgressBar } from './components/LocationTargetBoard.js';
import dashboardService from '../../services/DashboardService.js';
import Alert from '../common/Alert.js';
import { formatCurrencyWhole } from '../../utils/formatCurrency.js';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'in_target', label: 'In target' },
  { id: 'try_to_reach', label: 'Try to reach' },
  { id: 'no_target', label: 'No target' },
];

const groupOf = (status) => {
  if (status === 'ahead') return 'in_target';
  if (status === 'on_track' || status === 'behind') return 'try_to_reach';
  return 'no_target';
};

const PILL = {
  in_target: 'bg-sky-100 text-sky-800',
  try_to_reach: 'bg-sky-50 text-sky-700',
  no_target: 'bg-slate-100 text-slate-500',
};

const PILL_LABEL = {
  in_target: 'In target',
  try_to_reach: 'Try to reach',
  no_target: 'No target',
};

const dateParams = (dateRange, customStart, customEnd) => {
  const params = { date_range: dateRange || 'this_month' };
  if (dateRange === 'custom') {
    if (customStart) params.from_date = customStart;
    if (customEnd) params.to_date = customEnd;
  }
  return params;
};

const rankRows = (rows) => [...rows].sort((left, right) => {
  const leftOpen = left.status === 'no_target' ? 1 : 0;
  const rightOpen = right.status === 'no_target' ? 1 : 0;
  if (leftOpen !== rightOpen) return leftOpen - rightOpen;
  const progressGap = (right.progress_pct || 0) - (left.progress_pct || 0);
  if (progressGap !== 0) return progressGap;
  return String(left.name || '').localeCompare(String(right.name || ''));
}).map((row, index) => ({ ...row, rank: index + 1 }));

const LocationTargetsPage = () => {
  const [dateRange, setDateRange] = useState('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [peopleByHub, setPeopleByHub] = useState({});

  useEffect(() => {
    let cancelled = false;
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const load = async () => {
      try {
        setLoading(true);
        const response = await dashboardService.getLocationTargets(
          dateParams(dateRange, customStart, customEnd),
        );
        if (!cancelled) {
          setBoard(response?.data || null);
          setPeopleByHub({});
          setOpenId(null);
        }
      } catch (error) {
        if (!cancelled) {
          setBoard(null);
          Alert.error(error?.response?.data?.error || 'Could not load location targets');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [dateRange, customStart, customEnd]);

  const ranked = useMemo(() => rankRows(board?.rows || []), [board]);
  const counts = useMemo(() => ({
    all: ranked.length,
    in_target: ranked.filter((row) => groupOf(row.status) === 'in_target').length,
    try_to_reach: ranked.filter((row) => groupOf(row.status) === 'try_to_reach').length,
    no_target: ranked.filter((row) => groupOf(row.status) === 'no_target').length,
  }), [ranked]);
  const visible = filter === 'all'
    ? ranked
    : ranked.filter((row) => groupOf(row.status) === filter);

  const toggle = async (hubId) => {
    if (openId === hubId) {
      setOpenId(null);
      return;
    }
    setOpenId(hubId);
    if (peopleByHub[hubId]) return;
    setPeopleByHub((current) => ({ ...current, [hubId]: { loading: true, people: [] } }));
    try {
      const response = await dashboardService.getLocationTargetContributors(
        hubId,
        dateParams(dateRange, customStart, customEnd),
      );
      setPeopleByHub((current) => ({
        ...current,
        [hubId]: { loading: false, people: response?.data?.contributors || [] },
      }));
    } catch (error) {
      setPeopleByHub((current) => ({ ...current, [hubId]: { loading: false, people: [] } }));
      Alert.error(error?.response?.data?.error || 'Could not load who booked at this location');
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Branch performance"
        subtitle="Daily target is the monthly target divided by 26 working days. Target till date uses the working days elapsed, and variance is achievement minus that target. Sundays are off."
      >
        <DateRangeFilter
          wide
          value={dateRange}
          onChange={setDateRange}
          customStart={customStart}
          customEnd={customEnd}
          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
        />
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((item) => {
          const active = filter === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setFilter(item.id)}
              className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                active
                  ? 'bg-sky-600 text-white border-sky-600'
                  : 'bg-sky-50 text-sky-800 border-sky-100 hover:bg-sky-100'
              }`}
            >
              {item.label}
              <span className={`ml-1.5 ${active ? 'text-white/80' : 'text-sky-500'}`}>{counts[item.id] || 0}</span>
            </button>
          );
        })}
      </div>

      {loading && ranked.length === 0 ? (
        <p className="text-sm text-sky-700/70">Loading location targets…</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-sky-700/70">No location matches this view.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
          {visible.map((row) => {
            const open = openId === row.location_hub_id;
            const peopleState = peopleByHub[row.location_hub_id];
            const group = groupOf(row.status);
            return (
              <article
                key={row.location_hub_id}
                className={`rounded-xl border bg-white shadow-sm overflow-hidden ${
                  open ? 'border-sky-300 ring-1 ring-sky-200' : 'border-sky-100'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggle(row.location_hub_id)}
                  className="w-full text-left p-3 bg-gradient-to-b from-sky-50 to-white"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-sky-950 break-words leading-snug">{row.name}</p>
                    <span className="shrink-0 text-[11px] font-bold text-sky-600">#{row.rank}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${PILL[group]}`}>
                      {PILL_LABEL[group]}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-sky-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </div>
                  <BranchPerformanceTable row={row} asOf={board?.to_date} />
                  <div className="mt-2">
                    <TargetProgressBar percent={row.progress_pct} />
                    <p className="mt-1 text-[11px] font-semibold text-sky-700">
                      {row.progress_pct == null
                        ? 'No target set'
                        : `${row.progress_pct}% of target till date`}
                    </p>
                  </div>
                </button>

                {open ? (
                  <div className="px-3 pb-3 border-t border-sky-100 bg-sky-50/60">
                    {peopleState?.loading ? (
                      <p className="text-xs text-sky-700/70 py-2">Loading bookings…</p>
                    ) : (peopleState?.people || []).length === 0 ? (
                      <p className="text-xs text-sky-700/70 py-2">No bookings for this period.</p>
                    ) : (
                      <ul className="pt-2 space-y-1.5">
                        {peopleState.people.map((person) => (
                          <li key={person.user_id} className="flex items-start justify-between gap-2 text-xs">
                            <div className="min-w-0">
                              <p className="font-semibold text-sky-950 truncate">{person.name}</p>
                              <p className="text-sky-700/70">{person.role} · {person.shipments} shipments</p>
                            </div>
                            <p className="shrink-0 font-semibold text-sky-900 tabular-nums">
                              {formatCurrencyWhole(person.revenue)}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
};

export default LocationTargetsPage;
